// src/pages/polls/PollsAdminPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { io } from "socket.io-client";
import { POLLS_API, adminAuthHeader } from "./pollsApi";

function HoverButton({ style, hoverStyle, disabledStyle, disabled, onClick, children, title }) {
    const [hover, setHover] = useState(false);

    const s = {
        ...(style || {}),
        ...(hover && !disabled ? (hoverStyle || {}) : {}),
        ...(disabled ? (disabledStyle || {}) : {})
    };

    return (
        <button
            title={title}
            style={s}
            disabled={disabled}
            onClick={disabled ? undefined : onClick}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
        >
            {children}
        </button>
    );
}

export default function PollsAdminPage() {
    const [roomCode, setRoomCode] = useState("gt3x-r1");
    const [questions, setQuestions] = useState([]);

    // Live (Socket)
    const [liveActive, setLiveActive] = useState(null);
    const [liveResults, setLiveResults] = useState(null);
    const [liveStatus, setLiveStatus] = useState("offline"); // offline | connecting | online

    const [newQ, setNewQ] = useState({
        order: 1,
        title: "",
        timestamp: "",
        mediaUrl: "",
        options: ["Fahrer A", "Fahrer B", "Racing Incident", "Unklar"]
    });

    const socketRef = useRef(null);

    const roomUrl = useMemo(
        () => `${window.location.origin}/polls/room?code=${encodeURIComponent(roomCode)}`,
        [roomCode]
    );
    const overlayUrl = useMemo(
        () => `${window.location.origin}/polls/overlay?code=${encodeURIComponent(roomCode)}`,
        [roomCode]
    );

    async function loadQuestions(rc) {
        try {
            const r = await fetch(
                `${POLLS_API}/api/admin/questions/${encodeURIComponent(rc)}`,
                { headers: { ...adminAuthHeader() } }
            );
            const j = await r.json().catch(() => null);

            if (r.status === 401 || r.status === 403) {
                return alert("Keine Admin-Berechtigung (bitte im Manager einloggen).");
            }
            if (!r.ok || !j?.ok) return alert(j?.error || `Konnte Fragen nicht laden (${r.status})`);

            setQuestions(j.questions || []);
        } catch (err) {
            console.error("loadQuestions error:", err);
            alert(`Konnte Fragen nicht laden: ${err?.message || err}`);
        }
    }

    // 👉 NEU: Live-State via HTTP synchronisieren (damit Live Preview nie "hängen bleibt")
    async function syncLiveState(rc) {
        try {
            const r = await fetch(`${POLLS_API}/api/public/state/${encodeURIComponent(rc)}`);
            const j = await r.json().catch(() => null);
            if (!r.ok || !j?.ok) return;

            // j.active kann null sein
            setLiveActive(j.active || null);
            setLiveResults(j.results || null);
        } catch (err) {
            // bewusst kein alert – ist nur sync helper
            console.debug("syncLiveState failed:", err?.message || err);
        }
    }

    async function createQuestion() {
        try {
            const r = await fetch(`${POLLS_API}/api/admin/questions`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...adminAuthHeader() },
                body: JSON.stringify({ roomCode, ...newQ })
            });

            const j = await r.json().catch(() => null);
            if (r.status === 401 || r.status === 403) {
                return alert("Keine Admin-Berechtigung (bitte im Manager einloggen).");
            }
            if (!r.ok || !j?.ok) return alert(j?.error || `Fehler beim Erstellen (${r.status})`);

            setNewQ((p) => ({
                ...p,
                title: "",
                timestamp: "",
                mediaUrl: "",
                order: (p.order || 0) + 1
            }));

            await loadQuestions(roomCode);
        } catch (err) {
            console.error("createQuestion error:", err);
            alert(`Fehler beim Erstellen: ${err?.message || err}`);
        }
    }

    async function activate(questionId) {
        try {
            const r = await fetch(`${POLLS_API}/api/admin/activate`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...adminAuthHeader() },
                body: JSON.stringify({ roomCode, questionId })
            });

            const j = await r.json().catch(() => null);
            if (r.status === 401 || r.status === 403) {
                return alert("Keine Admin-Berechtigung (bitte im Manager einloggen).");
            }
            if (!r.ok || !j?.ok) return alert(j?.error || `Aktivieren fehlgeschlagen (${r.status})`);

            await loadQuestions(roomCode);

            // NEU: sofortiger Sync, falls Socket Event verzögert ankommt
            await syncLiveState(roomCode);
        } catch (err) {
            console.error("activate error:", err);
            alert(`Aktivieren fehlgeschlagen: ${err?.message || err}`);
        }
    }

    async function showResults(questionId, show) {
        try {
            const r = await fetch(`${POLLS_API}/api/admin/showResults`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...adminAuthHeader() },
                body: JSON.stringify({ roomCode, questionId, show })
            });

            const j = await r.json().catch(() => null);
            if (r.status === 401 || r.status === 403) {
                return alert("Keine Admin-Berechtigung (bitte im Manager einloggen).");
            }
            if (!r.ok || !j?.ok) return alert(j?.error || `Show/Hide fehlgeschlagen (${r.status})`);

            await loadQuestions(roomCode);

            // NEU: Live Sync
            await syncLiveState(roomCode);
        } catch (err) {
            console.error("showResults error:", err);
            alert(`Show/Hide fehlgeschlagen: ${err?.message || err}`);
        }
    }

    async function copy(text) {
        try {
            await navigator.clipboard.writeText(text);
            alert("Link kopiert ✅");
        } catch {
            const ta = document.createElement("textarea");
            ta.value = text;
            document.body.appendChild(ta);
            ta.select();
            document.execCommand("copy");
            document.body.removeChild(ta);
            alert("Link kopiert ✅");
        }
    }

    // Initial load
    useEffect(() => {
        loadQuestions(roomCode);
        syncLiveState(roomCode);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Live Socket im Admin + robuste Resyncs
    useEffect(() => {
        if (!roomCode) return;

        setLiveStatus("connecting");

        // vorhandenen Socket sauber schließen
        if (socketRef.current) {
            try { socketRef.current.disconnect(); } catch {}
            socketRef.current = null;
        }

        const s = io(POLLS_API, { transports: ["websocket"] });
        socketRef.current = s;

        s.on("connect", async () => {
            setLiveStatus("online");
            s.emit("joinRoom", { roomCode });
            // NEU: beim Connect sofort aktuellen State holen
            await syncLiveState(roomCode);
        });

        s.on("disconnect", () => setLiveStatus("offline"));

        // Wenn Backend eventet: liveActive/liveResults updaten
        s.on("activeQuestion", (q) => {
            setLiveActive(q || null);
            // beim Wechsel: results nicht auf "alte" stehen lassen
            setLiveResults(null);
        });

        s.on("results", (r) => setLiveResults(r || null));

        // NEU: als fallback auch bei roomCode Wechsel einmal syncen
        syncLiveState(roomCode);

        return () => {
            try { s.disconnect(); } catch {}
            socketRef.current = null;
        };
    }, [roomCode]);

    // Styles
    const wrap = { padding: 24, fontFamily: "system-ui", color: "#fff" };

    const panel = {
        background: "rgba(35,47,45,0.78)",         // #232F2D
        border: "1px solid rgba(228,254,60,0.20)", // #E4FE3C
        borderRadius: 18,
        padding: 18,
        maxWidth: 1100,
        margin: "0 auto",
        boxShadow: "0 16px 50px rgba(0,0,0,0.30)"
    };

    const inp = {
        width: "100%",
        padding: "12px 12px",
        borderRadius: 12,
        border: "1px solid rgba(255,255,255,0.14)",
        background: "rgba(0,0,0,0.25)",
        color: "#fff",
        outline: "none"
    };

    const btnBase = {
        padding: "12px 14px",
        borderRadius: 12,
        border: "1px solid rgba(228,254,60,0.35)",
        background: "rgba(228,254,60,0.18)",
        color: "#fff",
        cursor: "pointer",
        fontWeight: 900,
        transition: "transform 0.08s ease, background 0.12s ease, border-color 0.12s ease",
    };

    const btnSmall = { ...btnBase, padding: "10px 12px", fontWeight: 800 };

    const btnHover = {
        background: "rgba(228,254,60,0.28)",
        border: "1px solid rgba(228,254,60,0.55)",
        transform: "translateY(-1px)"
    };

    const btnDisabled = {
        opacity: 0.5,
        cursor: "not-allowed",
        transform: "none"
    };

    const qCard = {
        padding: 14,
        borderRadius: 16,
        background: "rgba(0,0,0,0.22)",
        border: "1px solid rgba(255,255,255,0.10)"
    };

    const chip = {
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 10px",
        borderRadius: 999,
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(0,0,0,0.20)",
        fontSize: 13,
        opacity: 0.95
    };

    const livePill = {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        background: "rgba(228,254,60,0.18)",
        border: "1px solid rgba(228,254,60,0.45)",
        color: "#E4FE3C",
        fontWeight: 900,
        fontSize: 12,
        letterSpacing: 0.5
    };

    const liveTotal = liveResults?.total ?? 0;

    return (
        <div style={wrap}>
            <div style={panel}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>RaceVision Polls Admin</h2>
                    <div style={{ opacity: 0.75, fontSize: 13 }}>Central Admin Login aktiv</div>
                </div>

                <div style={{ marginTop: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <div style={chip}>
                        <span style={{ opacity: 0.75 }}>Socket:</span>
                        <b style={{ color: liveStatus === "online" ? "#E4FE3C" : "#fff" }}>{liveStatus}</b>
                    </div>
                    <div style={chip}>
                        <span style={{ opacity: 0.75 }}>Backend:</span>
                        <b>{POLLS_API}</b>
                    </div>
                </div>

                <div style={{ marginTop: 12, opacity: 0.9, display: "grid", gap: 8 }}>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ opacity: 0.85 }}>Viewer:</span>
                        <b style={{ wordBreak: "break-all" }}>{roomUrl}</b>
                        <HoverButton style={btnSmall} hoverStyle={btnHover} disabledStyle={btnDisabled} onClick={() => copy(roomUrl)}>
                            Copy
                        </HoverButton>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ opacity: 0.85 }}>Overlay:</span>
                        <b style={{ wordBreak: "break-all" }}>{overlayUrl}</b>
                        <HoverButton style={btnSmall} hoverStyle={btnHover} disabledStyle={btnDisabled} onClick={() => copy(overlayUrl)}>
                            Copy
                        </HoverButton>
                    </div>
                </div>

                <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
                    <input
                        style={inp}
                        value={roomCode}
                        onChange={(e) => setRoomCode(e.target.value)}
                        placeholder="roomCode z.B. gt3x-r1"
                    />
                    <HoverButton
                        style={btnSmall}
                        hoverStyle={btnHover}
                        disabledStyle={btnDisabled}
                        onClick={() => { loadQuestions(roomCode); syncLiveState(roomCode); }}
                    >
                        Laden
                    </HoverButton>
                </div>

                {/* Live Preview */}
                <div style={{ marginTop: 12 }}>
                    {liveActive ? (
                        <div style={{ ...qCard, border: "1px solid rgba(228,254,60,0.20)" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
                                <div style={{ fontWeight: 900, fontSize: 16 }}>
                                    <span style={{ ...livePill, marginRight: 10 }}>LIVE</span>
                                    {liveActive.title}
                                    {liveActive.timestamp ? (
                                        <span style={{ marginLeft: 8, opacity: 0.8 }}>({liveActive.timestamp})</span>
                                    ) : null}
                                </div>
                                <div style={{ opacity: 0.85 }}>
                                    Stimmen: <b>{liveTotal}</b>
                                </div>
                            </div>

                            {liveResults?.counts ? (
                                <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
                                    {liveActive.options.map((opt) => {
                                        const c = liveResults.counts?.[opt] || 0;
                                        const total = liveResults.total || 0;
                                        const pct = total ? Math.round((c / total) * 100) : 0;

                                        return (
                                            <div key={opt}>
                                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                                    <span>{opt}</span>
                                                    <span style={{ opacity: 0.85 }}>{pct}% ({c})</span>
                                                </div>
                                                <div style={{ height: 10, borderRadius: 999, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                                                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 999, background: "rgba(228,254,60,0.85)" }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div style={{ marginTop: 8, opacity: 0.8 }}>
                                    Warte auf Live-Ergebnis…
                                </div>
                            )}
                        </div>
                    ) : (
                        <div style={{ ...qCard, opacity: 0.85 }}>
                            Kein aktives Voting.
                        </div>
                    )}
                </div>

                <hr style={{ margin: "18px 0", opacity: 0.25 }} />

                <h3>Neue Frage</h3>
                <div style={{ display: "grid", gridTemplateColumns: "160px 1fr", gap: 10 }}>
                    <div style={{ opacity: 0.75, paddingTop: 10 }}>Order</div>
                    <input
                        style={inp}
                        value={newQ.order}
                        onChange={(e) => setNewQ((p) => ({ ...p, order: Number(e.target.value || 0) }))}
                    />

                    <div style={{ opacity: 0.75, paddingTop: 10 }}>Titel</div>
                    <input
                        style={inp}
                        value={newQ.title}
                        onChange={(e) => setNewQ((p) => ({ ...p, title: e.target.value }))}
                    />

                    <div style={{ opacity: 0.75, paddingTop: 10 }}>Timestamp</div>
                    <input
                        style={inp}
                        value={newQ.timestamp}
                        onChange={(e) => setNewQ((p) => ({ ...p, timestamp: e.target.value }))}
                        placeholder="z.B. 12:34 oder 1:02:18"
                    />

                    <div style={{ opacity: 0.75, paddingTop: 10 }}>Media URL</div>
                    <input
                        style={inp}
                        value={newQ.mediaUrl}
                        onChange={(e) => setNewQ((p) => ({ ...p, mediaUrl: e.target.value }))}
                        placeholder="Bildlink (optional)"
                    />
                </div>

                <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    {newQ.options.map((o, i) => (
                        <input
                            key={i}
                            style={inp}
                            value={o}
                            onChange={(e) =>
                                setNewQ((p) => {
                                    const arr = [...p.options];
                                    arr[i] = e.target.value;
                                    return { ...p, options: arr };
                                })
                            }
                            placeholder={`Option ${i + 1}`}
                        />
                    ))}
                </div>

                <HoverButton style={{ ...btnBase, marginTop: 12 }} hoverStyle={btnHover} disabledStyle={btnDisabled} onClick={createQuestion}>
                    Frage speichern
                </HoverButton>

                <hr style={{ margin: "18px 0", opacity: 0.25 }} />

                <h3>Fragen</h3>
                <div style={{ display: "grid", gap: 10 }}>
                    {questions.map((q) => (
                        <div key={q.id} style={qCard}>
                            <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                                <div>
                                    <div style={{ fontWeight: 900, display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
                                        {/* ✅ LIVE Badge vorne */}
                                        {q.isActive ? <span style={livePill}>LIVE</span> : null}

                                        <span>
                      #{q.order} · {q.title}
                                            {q.timestamp ? <span style={{ marginLeft: 10, opacity: 0.8 }}>({q.timestamp})</span> : null}
                                            {q.showResults ? <span style={{ marginLeft: 10, opacity: 0.85 }}>(Results ON)</span> : null}
                    </span>
                                    </div>

                                    <div style={{ opacity: 0.8, marginTop: 6 }}>
                                        {q.options.join(" · ")}
                                    </div>
                                </div>

                                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                                    <HoverButton
                                        style={btnSmall}
                                        hoverStyle={btnHover}
                                        disabledStyle={btnDisabled}
                                        onClick={() => activate(q.id)}
                                        title="Diese Frage LIVE schalten"
                                    >
                                        Aktivieren
                                    </HoverButton>

                                    <HoverButton
                                        style={btnSmall}
                                        hoverStyle={btnHover}
                                        disabledStyle={btnDisabled}
                                        onClick={() => showResults(q.id, false)}
                                        title="Ergebnisse ausblenden"
                                    >
                                        Hide
                                    </HoverButton>

                                    <HoverButton
                                        style={btnSmall}
                                        hoverStyle={btnHover}
                                        disabledStyle={btnDisabled}
                                        onClick={() => showResults(q.id, true)}
                                        title="Ergebnisse anzeigen"
                                    >
                                        Show
                                    </HoverButton>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

            </div>
        </div>
    );
}